import { useState } from 'react';
import { accountApi } from '../../api/client';

/**
 * Email change section — only rendered for admin/ops users.
 *
 * The customer self-serve flow is intentionally NOT exposed yet;
 * this section gates on `user.role` to keep scope tight while we
 * gather UX feedback from operators.
 */
export default function EmailChangeSection({ user }) {
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const role = (user?.role || '').toLowerCase();
  if (role !== 'admin' && role !== 'ops') return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (!newEmail.trim() || !currentPassword) {
      setError('Vul beide velden in.');
      return;
    }
    setSubmitting(true);
    try {
      await accountApi.requestEmailChange({
        newEmail: newEmail.trim(),
        currentPassword,
      });
      setSuccess(true);
      setNewEmail('');
      setCurrentPassword('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="email-change-section"
      className="mt-10 pt-10 border-t border-border"
    >
      <h2 className="font-serif text-xl font-light mb-1">E-mailadres wijzigen</h2>
      <p className="text-sm text-muted-foreground mb-5">
        Verander het inlog-e-mailadres van dit account.
        We sturen een bevestigingslink naar het nieuwe adres.
      </p>

      {success && (
        <div
          data-testid="email-change-success"
          className="text-sm bg-primary/5 border border-primary/20 text-primary px-4 py-3 rounded-lg mb-4"
        >
          Controleer je nieuwe mailbox en bevestig de wijziging.
        </div>
      )}
      {error && (
        <div
          data-testid="email-change-error"
          className="text-sm bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg mb-4"
        >
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4 max-w-md">
        <div>
          <label className="block text-sm font-medium mb-1">Nieuw e-mailadres</label>
          <input
            data-testid="email-change-new-email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoComplete="email"
            placeholder="naam@theherborist.be"
            className="w-full px-4 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Huidig wachtwoord</label>
          <input
            data-testid="email-change-current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            className="w-full px-4 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <button
          data-testid="email-change-submit"
          type="submit"
          disabled={submitting}
          className="bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50"
        >
          {submitting ? 'Bezig...' : 'Stuur bevestigingslink'}
        </button>
      </form>
    </section>
  );
}
